import { useEffect, useMemo, useRef, useState } from 'react';
import { apiService, OrderMinimumsReport } from '../services/api';

export interface MinimumsLine {
  productId: string;
  variantId?: string;
  quantity: number;
  /** unit price incl. add-ons × quantity */
  lineTotal: number;
}

/**
 * Live product-minimum-quantity / vendor-minimum-amount status for a set of
 * order lines — the same check the server runs when the order is created
 * (POST /api/checkout/order-minimums). Debounced; `extraProductIds` reports
 * minimums for products not in the order yet (e.g. the product on screen).
 */
export function useOrderMinimums(
  businessId: string | undefined,
  lines: MinimumsLine[],
  currency?: string,
  extraProductIds: string[] = []
) {
  const [report, setReport] = useState<OrderMinimumsReport | null>(null);
  const [loading, setLoading] = useState(false);
  const seq = useRef(0);

  const key = useMemo(
    () =>
      JSON.stringify([
        businessId,
        currency,
        lines.map((l) => [l.productId, l.variantId, l.quantity, Math.round(l.lineTotal * 100)]),
        [...extraProductIds].sort(),
      ]),
    [businessId, currency, lines, extraProductIds]
  );

  useEffect(() => {
    if (!businessId || (lines.length === 0 && extraProductIds.length === 0)) {
      setReport(null);
      return;
    }
    const mySeq = ++seq.current;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await apiService.checkOrderMinimums({ businessId, currency, items: lines, productIds: extraProductIds });
        if (mySeq === seq.current && res.statusCode === 1000 && res.data) setReport(res.data);
      } catch {
        // Non-blocking — the server still enforces minimums on submit.
      } finally {
        if (mySeq === seq.current) setLoading(false);
      }
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return { report, loading, ok: report ? report.ok : true };
}
